import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ProductItem from '../../components/Products/ProductItem';
import { getCategories, getProducts } from '../../components/Api/api';
import { useCart } from '../../context/CartContext';
import { useLanguage } from '../../context/LanguageContext';
import { getProductPath } from '../../utils/productUrl';
import './Home.css';
import LoadingState from '../../components/LoadingState/LoadingState';
import { getCategoryNavigationPath } from '../../utils/catalogLanding';

const HERO_BACKGROUND = '/images/hero.png';

const CATEGORY_THEMES = ['theme-air', 'theme-phone', 'theme-charge', 'theme-watch'];

const fallbackCategoryImage = '/images/categories_background2.jpg';

const normalizeImage = (image, fallback) => {
  if (!image) return fallback;
  return `${image}`.replace(/\\/g, '/');
};

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addItem } = useCart();
  const { t } = useLanguage();
  const bestSellerSectionRef = useRef(null);
  const categoriesSectionRef = useRef(null);
  const categoriesViewportRef = useRef(null);
  const categoriesTrackRef = useRef(null);
  const [bestSellers, setBestSellers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [bestSellersLoading, setBestSellersLoading] = useState(true);
  const [categoryIndex, setCategoryIndex] = useState(0);
  const [categoryStep, setCategoryStep] = useState(0);
  const [visibleCategories, setVisibleCategories] = useState(1);
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);
  const [isMobileCategoryRail, setIsMobileCategoryRail] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    getCategories()
      .then((response) => {
        if (!isCancelled) {
          setCategories(Array.isArray(response.data) ? response.data : []);
        }
      })
      .catch((error) => console.error('Error fetching home categories:', error))
      .finally(() => { if (!isCancelled) setCategoriesLoading(false); });

    getProducts({ params: { top_only: true, limit: 8 } })
      .then((response) => {
        if (!isCancelled) {
          setBestSellers(Array.isArray(response.data) ? response.data : []);
        }
      })
      .catch((error) => console.error('Error fetching home products:', error))
      .finally(() => { if (!isCancelled) setBestSellersLoading(false); });

    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    const measureCategories = () => {
      const track = categoriesTrackRef.current;
      const viewport = categoriesViewportRef.current;
      const firstCard = track?.querySelector('.category-tile');
      const mobileRail = window.innerWidth <= 900;

      setIsMobileCategoryRail(mobileRail);

      if (!track || !viewport || !firstCard) {
        setCategoryStep(0);
        setVisibleCategories(1);
        setActiveCategoryIndex(0);
        return;
      }

      const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || '0');
      const cardWidth = firstCard.getBoundingClientRect().width + gap;
      const viewportWidth = viewport.getBoundingClientRect().width;
      const itemsVisible = Math.max(1, Math.floor((viewportWidth + gap) / cardWidth));

      setCategoryStep(cardWidth);
      setVisibleCategories(itemsVisible);

      if (mobileRail) {
        setCategoryIndex(0);
      }
    };

    measureCategories();
    window.addEventListener('resize', measureCategories);

    return () => {
      window.removeEventListener('resize', measureCategories);
    };
  }, [categories]);

  const maxCategoryIndex = useMemo(
    () => Math.max(0, categories.length - visibleCategories),
    [categories.length, visibleCategories]
  );

  useEffect(() => {
    setCategoryIndex((prev) => Math.min(prev, maxCategoryIndex));
  }, [maxCategoryIndex]);

  useEffect(() => {
    const viewport = categoriesViewportRef.current;
    if (!viewport) return undefined;

    const handleScroll = () => {
      if (!categoryStep) return;

      if (isMobileCategoryRail) {
        const nextIndex = Math.min(categories.length - 1, Math.max(0, Math.round(viewport.scrollLeft / categoryStep)));
        setActiveCategoryIndex(nextIndex);
      } else {
        setActiveCategoryIndex(Math.min(maxCategoryIndex, categoryIndex));
      }
    };

    handleScroll();
    viewport.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      viewport.removeEventListener('scroll', handleScroll);
    };
  }, [categories.length, categoryIndex, categoryStep, isMobileCategoryRail, maxCategoryIndex]);

  const handleCategoryDotClick = (index) => {
    const viewport = categoriesViewportRef.current;

    if (isMobileCategoryRail) {
      if (!viewport || !categoryStep) return;

      viewport.scrollTo({
        left: index * categoryStep,
        behavior: 'smooth',
      });
      return;
    }

    setCategoryIndex(Math.min(maxCategoryIndex, Math.max(0, index)));
  };

  const handleProductClick = (product) => {
    if (product?.id) navigate(getProductPath(product));
  };


  const canSlideCategories = categories.length > visibleCategories;

  useEffect(() => {
    if (!location.hash) return;

    const sectionMap = {
      '#home-categories': categoriesSectionRef.current,
      '#home-promotions': bestSellerSectionRef.current,
    };

    const target = sectionMap[location.hash];
    if (!target) return;

    window.requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [categories.length, bestSellers.length, location.hash]);

  return (
    <div className="home-v3">
      <div className="home-shell">
        <section
          className="hero-banner"
          style={{
            '--hero-image': `url(${HERO_BACKGROUND})`,
          }}
        >
          <div className="hero-content">
            <span className="hero-pill">{t('home.welcome')}</span>
            <h1>
              {t('home.titleStart')}
              <br />
              <span>{t('home.titleHighlight')}</span>
            </h1>
            <div className="hero-actions">
              <button
                type="button"
                className="hero-primary"
                onClick={() => navigate('/shop')}
              >
                {t('home.shopNow')}
                <i className="fas fa-arrow-right" aria-hidden="true" />
              </button>
              <button
                type="button"
                className="hero-secondary"
                onClick={() =>
                  bestSellerSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
              >
                {t('home.seePromotions')}
              </button>
            </div>

          </div>
        </section>


        <section className="home-block" id="home-categories" ref={categoriesSectionRef}>
          <div className="section-title-row">
            <div>
              <h2>{t('home.categoryTitle')}</h2>
              <p>{t('home.categorySubtitle')}</p>
            </div>
          </div>

          <div className="categories-shell">
            {categoriesLoading && <LoadingState compact />}
            {canSlideCategories && (
              <button
                type="button"
                className="category-arrow left"
                onClick={() => setCategoryIndex((prev) => Math.max(0, prev - 1))}
                disabled={categoryIndex === 0}
                aria-label="Categories precedentes"
              >
                <i className="fas fa-chevron-left" aria-hidden="true" />
              </button>
            )}

            <div className="category-viewport" ref={categoriesViewportRef}>
              <div
                className="category-grid-v2"
                ref={categoriesTrackRef}
                style={{
                  transform: `translateX(-${categoryIndex * categoryStep}px)`,
                }}
              >
                {categories.map((category, index) => (
                  <Link
                    key={category.id || `${category.name}-${index}`}
                    className={`category-tile ${CATEGORY_THEMES[index % CATEGORY_THEMES.length]}`}
                    to={getCategoryNavigationPath(category)}
                  >
                    <img
                      src={normalizeImage(category.image, fallbackCategoryImage)}
                      alt={category.name}
                      loading="lazy"
                    />
                    <div className="category-tile-copy">
                      <h3>{category.name}</h3>
                      <span>
                        Explorer
                        <i className="fas fa-arrow-right" aria-hidden="true" />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {canSlideCategories && (
              <button
                type="button"
                className="category-arrow right"
                onClick={() => setCategoryIndex((prev) => Math.min(maxCategoryIndex, prev + 1))}
                disabled={categoryIndex >= maxCategoryIndex}
                aria-label="Categories suivantes"
              >
                <i className="fas fa-chevron-right" aria-hidden="true" />
              </button>
            )}
          </div>

          {categories.length > 1 && (
            <div className="mobile-rail-dots category-rail-dots" aria-label="Pagination des categories">
              {categories.map((category, index) => (
                <button
                  key={category.id || index}
                  type="button"
                  className={`mobile-rail-dot${index === activeCategoryIndex ? ' is-active' : ''}`}
                  aria-label={`Categorie ${index + 1}`}
                  aria-pressed={index === activeCategoryIndex}
                  onClick={() => handleCategoryDotClick(index)}
                />
              ))}
            </div>
          )}
        </section>

        {(bestSellersLoading || bestSellers.length > 0) && (
          <section className="home-block" id="home-promotions" ref={bestSellerSectionRef}>
            <div className="section-title-row">
              <div>
                <h2>{t('home.bestSellers')}</h2>
                <p>Nos produits les plus populaires cette semaine</p>
              </div>
              <button type="button" className="inline-link" onClick={() => navigate('/shop')}>
                {t('common.viewProduct')}
                <i className="fas fa-arrow-right" aria-hidden="true" />
              </button>
            </div>

            {bestSellersLoading ? <LoadingState cards /> : <div className="seller-grid">
              {bestSellers.map((item, index) => (
                <ProductItem
                  key={item.id}
                  image={item.main_photo}
                  name={item.name}
                  price={item.price}
                  isOnSale={item.is_promotion}
                  originalPrice={item.original_price}
                  extraAttributes={item.extra_attributes}
                  condition={item.condition}
                  cardVariantClass={
                    ['card-variant-peach', 'card-variant-mint', 'card-variant-soft'][index % 3]
                  }
                  badgeLabel={
                    item.is_promotion && Number(item.original_price) > Number(item.price)
                      ? t('home.sale')
                      : item.is_top_product
                        ? t('home.topSale')
                        : ''
                  }
                  onClick={() => handleProductClick(item)}
                  productPath={getProductPath(item)}
                  onAddToCart={() => addItem(item, 1)}
                />
              ))}
            </div>}
          </section>
        )}
      </div>
    </div>
  );
};

export default Home;
